import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "../../lib/api";
import { buildBookingUrl, goBooking, isScheduleActive } from "../../lib/promo";
import { useLang } from "../../lib/i18n";
import CaptionedImage from "../CaptionedImage";

// How long a closed popup stays hidden per visitor; configurable in Settings
const FREQUENCY_DAYS = { day: 1, "3days": 3, week: 7, month: 30 };

export default function PromoPopup({ settings, offers, popups }) {
  const { t } = useLang();
  const [ready, setReady] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [promoIndex, setPromoIndex] = useState(0);

  const dedicatedPopups = (popups || []).filter((item) => item.active !== false);
  const hasDedicatedPopups = dedicatedPopups.length > 0;
  const hasCustomContent = Boolean(settings?.promo_popup_title || settings?.promo_popup_text || settings?.promo_popup_image);
  const promotions = useMemo(() => {
    if (hasDedicatedPopups) {
      return [...dedicatedPopups]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((item) => ({ ...item, description: item.message || item.description || "" }));
    }
    if (hasCustomContent) {
      return [{
        id: "custom-popup",
        eyebrow: "Limited Privilege",
        title: settings?.promo_popup_title || "",
        description: settings?.promo_popup_text || "",
        image_url: settings?.promo_popup_image || "",
        image_alt: settings?.promo_popup_image_alt || "",
        image_title: settings?.promo_popup_image_title || "",
        image_description: settings?.promo_popup_image_description || "",
        image_caption: settings?.promo_popup_image_caption || "",
        image_focal_point: settings?.promo_popup_image_focal_point || "50% 0%",
        promo_code: settings?.promo_popup_promo_code || "",
        booking_url: settings?.promo_popup_cta_url || settings?.booking_engine_url || "",
        cta_label: settings?.promo_popup_cta_label || "",
        start_date: settings?.promo_popup_start_date || "",
        end_date: settings?.promo_popup_end_date || "",
      }];
    }
    return (offers || []).filter((offer) => isScheduleActive(offer.start_date, offer.end_date));
  }, [dedicatedPopups, hasDedicatedPopups, hasCustomContent, offers, settings]);

  const globalStart = hasDedicatedPopups ? "" : settings?.promo_popup_start_date || "";
  const globalEnd = hasDedicatedPopups ? "" : settings?.promo_popup_end_date || "";
  const activePromotions = promotions.filter((promo) =>
    isScheduleActive(globalStart || promo.start_date, globalEnd || promo.end_date)
  );
  const promo = activePromotions[promoIndex] || activePromotions[0];
  const enabled = settings?.promo_popup_enabled !== false;
  const ctaLabel = promo?.cta_label || settings?.promo_popup_cta_label || t("offers.book");

  const storageKey = useMemo(() => {
    const identity = activePromotions.map((item) => item.id || item.title).join("_") || "default";
    return `nl_promo_dismissed_${encodeURIComponent(identity)}`;
  }, [activePromotions]);

  useEffect(() => {
    setPromoIndex(0);
  }, [storageKey]);

  useEffect(() => {
    if (!enabled || !promo?.title) return undefined;
    const frequency = settings?.promo_popup_frequency || "week";
    if (frequency === "session") {
      if (sessionStorage.getItem(storageKey)) {
        setDismissed(true);
        return undefined;
      }
    } else if (frequency !== "always") {
      const dismissedAt = Number(localStorage.getItem(storageKey) || 0);
      const days = FREQUENCY_DAYS[frequency] ?? 7;
      if (dismissedAt && Date.now() - dismissedAt < days * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return undefined;
      }
    }
    const timer = window.setTimeout(() => setReady(true), 3500);
    const onScroll = () => {
      if (window.scrollY > 180) setScrolled(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled, promo?.title, storageKey]);

  if (!enabled || !promo?.title || !promo?.description || dismissed) return null;

  const bookingUrl = buildBookingUrl(promo.booking_url || settings?.booking_engine_url || "", promo.promo_code || "");
  const detailUrl = promo.detail_url || (!hasDedicatedPopups && promo.id && promo.id !== "custom-popup" ? `/offer/${promo.slug || promo.id}` : "");
  const close = () => {
    const frequency = settings?.promo_popup_frequency || "week";
    if (frequency === "session") sessionStorage.setItem(storageKey, "1");
    else if (frequency !== "always") localStorage.setItem(storageKey, String(Date.now()));
    setDismissed(true);
  };
  const openCta = () => {
    // Open the embedded /book page in the same tab; promo code is carried into the Exely engine
    if (bookingUrl) goBooking(bookingUrl);
    else close();
  };
  const previousPromo = () => setPromoIndex((value) => (value - 1 + activePromotions.length) % activePromotions.length);
  const nextPromo = () => setPromoIndex((value) => (value + 1) % activePromotions.length);

  return (
    <AnimatePresence>
      {ready && scrolled && (
        <motion.aside
          data-testid="promo-popup"
          role="dialog"
          aria-live="polite"
          aria-label={t("promo.aria")}
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.97 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="fixed left-4 right-4 bottom-24 sm:left-6 sm:right-auto sm:bottom-6 z-40 max-w-[380px] rounded-md border border-[#D8CBB8]/80 bg-[#FBF9F5]/95 backdrop-blur-md shadow-[0_24px_70px_rgba(30,26,23,0.18)] overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#9A7B66]" />
          <button
            data-testid="promo-popup-close"
            onClick={close}
            aria-label="Close promotion"
            className="absolute top-3 right-3 p-1.5 rounded-full text-[#A0988E] hover:text-[#2C2825] hover:bg-[#EFE8DE] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={promo.id || promo.title}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.35 }}
              className="flex gap-4 p-5 pr-11"
            >
              {promo.image_url && (
                <div className="w-[86px] h-[104px] rounded-sm overflow-hidden shrink-0 hidden sm:block">
                  <CaptionedImage
                    testId="promo-popup-image"
                    src={mediaUrl(promo.image_url)}
                    alt={promo.image_alt || promo.title || "Nusadira promotion"}
                    title={promo.image_title || promo.title}
                    description={promo.image_description || promo.description}
                    caption={promo.image_caption || ""}
                    focalPoint={promo.image_focal_point || "50% 0%"}
                    className="w-full h-full"
                    imageClassName="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-[10px] uppercase tracking-[0.24em] font-sans font-semibold text-[#9A7B66]">
                    {promo.eyebrow || t("promo.eyebrow")}
                  </p>
                  {activePromotions.length > 1 && (
                    <p data-testid="promo-popup-count" className="text-[10px] font-mono text-[#A0988E]">
                      {promoIndex + 1}/{activePromotions.length}
                    </p>
                  )}
                </div>
                <h2 data-testid="promo-popup-title" className="font-serif text-2xl leading-tight text-[#2C2825]">
                  {promo.title}
                </h2>
                <p data-testid="promo-popup-text" className="mt-2 text-xs sm:text-sm text-[#6E675F] font-sans leading-relaxed line-clamp-3">
                  {promo.description}
                </p>
                <div className="mt-4 flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <button
                      data-testid="promo-popup-cta"
                      onClick={openCta}
                      className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-sm bg-[#9A7B66] hover:bg-[#826552] text-white text-[11px] font-sans font-semibold uppercase tracking-[0.14em] transition-colors"
                    >
                      {ctaLabel} <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                    {detailUrl && (
                      detailUrl.startsWith("/") ? (
                        <Link
                          data-testid="promo-popup-details"
                          to={detailUrl}
                          className="text-[11px] font-sans font-semibold text-[#9A7B66] hover:text-[#826552] transition-colors"
                        >
                          Details
                        </Link>
                      ) : (
                        <a
                          data-testid="promo-popup-details"
                          href={detailUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] font-sans font-semibold text-[#9A7B66] hover:text-[#826552] transition-colors"
                        >
                          Details
                        </a>
                      )
                    )}
                  </div>
                  {activePromotions.length > 1 && (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex gap-1.5">
                        {activePromotions.map((item, index) => (
                          <button
                            key={item.id || item.title}
                            data-testid={`promo-popup-dot-${index}`}
                            onClick={() => setPromoIndex(index)}
                            aria-label={`Show promotion ${index + 1}`}
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                              index === promoIndex ? "w-6 bg-[#9A7B66]" : "w-1.5 bg-[#D8CBB8] hover:bg-[#9A7B66]/60"
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          data-testid="promo-popup-prev"
                          onClick={previousPromo}
                          aria-label="Previous promotion"
                          className="p-1.5 rounded-full text-[#9A7B66] hover:bg-[#EFE8DE] transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          data-testid="promo-popup-next"
                          onClick={nextPromo}
                          aria-label="Next promotion"
                          className="p-1.5 rounded-full text-[#9A7B66] hover:bg-[#EFE8DE] transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
